import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useSystemSettings } from "../lib/systemSettings";

const PRIVACY_VERSION = "1.0";
const TOS_VERSION = "1.0";

/**
 * Registrierung eines Kundenkontos.
 *
 * Der Pflicht-Consent besteht aus einer einzigen Checkbox, die den
 * Kunden darauf hinweist, Datenschutz zur Kenntnis genommen und AGB
 * akzeptiert zu haben. Beide Rechtstexte sind sprachlich getrennt
 * anklickbar und öffnen ein Modal, das denselben Text aus dem zentralen
 * CMS lädt wie das Kundenportal — es gibt keine zweite Fassung im
 * Frontend-Code.
 *
 * Newsletter-Opt-in ist eine zweite, standardmäßig NICHT gesetzte
 * Checkbox — technisch getrennt gespeichert (``customer_accounts.marketing_opt_in``).
 */
export default function Register() {
  const { brandShort, brandName } = useSystemSettings();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    password: "", password2: "",
    address_street: "", address_zip: "", address_city: "", address_country: "",
    date_of_birth: "",
    consent: false,
    marketing_opt_in: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);
  const [modal, setModal] = useState(null); // 'datenschutz' | 'agb' | null
  const [modalContent, setModalContent] = useState({ loading: false, text: "" });
  const nav = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function openLegal(kind) {
    setModal(kind);
    setModalContent({ loading: true, text: "" });
    try {
      const r = await api.get(`/portal/legal/${kind}`);
      setModalContent({ loading: false, text: r.data?.value || "" });
    } catch {
      setModalContent({ loading: false, text: "" });
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.consent) { toast.error("Bitte bestätige, dass du Datenschutz zur Kenntnis genommen und AGB akzeptiert hast"); return; }
    if (!form.date_of_birth) { toast.error("Bitte gib dein Geburtsdatum an"); return; }
    // Sanity-Frontend-Check: kein Zukunftsdatum, plausibles Jahr.
    const dob = new Date(form.date_of_birth);
    const today = new Date();
    if (Number.isNaN(dob.getTime()) || dob > today || dob.getFullYear() < 1900) {
      toast.error("Bitte gib ein gültiges Geburtsdatum an"); return;
    }
    if (form.password !== form.password2) { toast.error("Passwörter stimmen nicht überein"); return; }
    if (form.password.length < 12) { toast.error("Passwort muss mindestens 12 Zeichen lang sein"); return; }
    setSubmitting(true);
    try {
      await api.post("/customer-auth/register", {
        email: form.email.trim(), password: form.password,
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        address_street: form.address_street || null,
        address_zip: form.address_zip || null,
        address_city: form.address_city || null,
        address_country: form.address_country ? form.address_country.toUpperCase() : null,
        date_of_birth: form.date_of_birth,
        accept_privacy_version: PRIVACY_VERSION,
        accept_tos_version: TOS_VERSION,
        marketing_opt_in: form.marketing_opt_in,
      });
      setOk(true);
      toast.success("Registrierung erfolgreich. Bitte E-Mail bestätigen.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Registrierung fehlgeschlagen");
    } finally { setSubmitting(false); }
  }

  if (ok) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="register-success">
        <div className="max-w-md text-center space-y-4">
          <div className="text-2xl font-black uppercase tracking-tight">Fast geschafft</div>
          <div className="text-zinc-400">
            Wir haben dir eine E-Mail mit einem Bestätigungslink geschickt.
            Bitte klicke auf den Link, um dein {brandShort}-Konto zu aktivieren.
          </div>
          <Button onClick={() => nav("/login")} className="mt-4" data-testid="register-goto-login">Zum Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="register-form">
        <div>
          <div className="text-2xl font-black uppercase tracking-tight break-words">{brandShort} Club Konto</div>
          <div className="text-sm text-zinc-500 mt-1">Registriere dich als Kunde</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Vorname <span className="text-cyan-400">*</span></Label>
            <Input required data-testid="register-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></div>
          <div><Label>Nachname <span className="text-cyan-400">*</span></Label>
            <Input required data-testid="register-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></div>
        </div>
        <div><Label>E-Mail <span className="text-cyan-400">*</span></Label>
          <Input required type="email" data-testid="register-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
        <div><Label>Geburtsdatum <span className="text-cyan-400">*</span></Label>
          <Input required type="date" data-testid="register-date-of-birth"
            max={new Date().toISOString().slice(0, 10)}
            value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
          <div className="text-[11px] text-zinc-500 mt-1">
            Pflichtfeld. Wird nur intern für Altersfreigaben verwendet.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Passwort <span className="text-cyan-400">*</span> <span className="text-xs text-zinc-500">(≥12)</span></Label>
            <Input required type="password" minLength={12} data-testid="register-password" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
          <div><Label>Passwort bestätigen <span className="text-cyan-400">*</span></Label>
            <Input required type="password" minLength={12} data-testid="register-password2" value={form.password2} onChange={(e) => set("password2", e.target.value)} /></div>
        </div>
        <div className="pt-2 text-xs uppercase tracking-wider text-zinc-500">Anschrift (freiwillig)</div>
        <div data-testid="privacy-hint" className="text-xs text-zinc-500 border border-zinc-800 rounded-sm p-3 leading-relaxed">
          Die Angabe weiterer persönlicher Daten ist freiwillig. Informationen zur Verarbeitung
          deiner Daten findest du in unserer{" "}
          <button type="button" onClick={() => openLegal("datenschutz")}
                  className="text-cyan-400 hover:underline">Datenschutzerklärung</button>.
        </div>
        <div><Label>Straße</Label>
          <Input data-testid="register-street" value={form.address_street} onChange={(e) => set("address_street", e.target.value)} /></div>
        <div className="grid grid-cols-[1fr_2fr_5rem] gap-3">
          <div><Label>PLZ</Label>
            <Input data-testid="register-zip" value={form.address_zip} onChange={(e) => set("address_zip", e.target.value)} /></div>
          <div><Label>Ort</Label>
            <Input data-testid="register-city" value={form.address_city} onChange={(e) => set("address_city", e.target.value)} /></div>
          <div><Label>Land</Label>
            <Input maxLength={2} placeholder="DE" data-testid="register-country" value={form.address_country} onChange={(e) => set("address_country", e.target.value.toUpperCase())} /></div>
        </div>

        <label className="flex items-start gap-2 text-sm text-zinc-400 pt-2">
          <input required type="checkbox" data-testid="register-consent" checked={form.consent}
                 onChange={(e) => set("consent", e.target.checked)} className="mt-1" />
          <span>
            Ich habe die{" "}
            <button type="button" onClick={() => openLegal("datenschutz")}
                    data-testid="register-open-datenschutz"
                    className="text-cyan-400 hover:underline">Datenschutzerklärung</button>
            {" "}zur Kenntnis genommen und akzeptiere die{" "}
            <button type="button" onClick={() => openLegal("agb")}
                    data-testid="register-open-agb"
                    className="text-cyan-400 hover:underline">AGB</button>.
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm text-zinc-400">
          <input type="checkbox" data-testid="register-marketing-optin"
                 checked={form.marketing_opt_in}
                 onChange={(e) => set("marketing_opt_in", e.target.checked)} className="mt-1" />
          <span>
            Ich möchte per E-Mail über Neuigkeiten, Angebote und Aktionen von {brandName}
            informiert werden. <span className="text-zinc-500">(freiwillig, jederzeit widerrufbar)</span>
          </span>
        </label>

        <Button type="submit" disabled={submitting} data-testid="register-submit" className="w-full h-12 rounded-sm">
          {submitting ? "Registrieren..." : "Konto erstellen"}
        </Button>
        <div className="text-center text-sm text-zinc-500 pt-2">
          Schon ein Konto? <Link to="/login" className="text-cyan-400 hover:underline">Zum Login</Link>
        </div>
      </form>

      <Dialog open={modal !== null} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid={`legal-modal-${modal || ""}`} className="bg-zinc-950 border-zinc-800 sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {modal === "datenschutz" ? "Datenschutzerklärung" : "AGB"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Version aus dem zentralen CMS. Beim Bestätigen wird die aktuelle Version dokumentiert.
            </DialogDescription>
          </DialogHeader>
          {modalContent.loading ? (
            <div className="text-sm text-zinc-500">Lade…</div>
          ) : modalContent.text ? (
            <div className="whitespace-pre-wrap text-sm text-zinc-300 leading-relaxed">
              {modalContent.text}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-4">
              Text wird gerade gepflegt. Bitte später erneut prüfen oder den Betreiber kontaktieren.
            </div>
          )}
          <DialogFooter>
            <Button data-testid="legal-modal-close" onClick={() => setModal(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
